//src/app/features/services/addService/step-one/index.tsx
import React, { useState } from 'react';
import useDebouncedFilters from 'app/hooks/useDebouncedFilters';
import { useGetCartItemsQuery } from 'app/services/payments/cart';
import { useGetServicesQuery } from 'app/services/payments/services';
import { useAddCartItemServiceMutation } from 'app/services/payments/cart';
import { useGetUserBusinessesQuery } from 'app/services/user';
import { Service } from 'app/services/responseTypes';
import { NextIcon } from 'assets/icons';
import { CustomButton } from 'Components/Auth/CustomButton';
import Spinner from 'Components/Spinner';
import { successToast, errorToast } from 'Components/Notifications';
import ServiceCard from '../serviceCard';
import ServiceDetailsModal from './ServiceDetailsModal'; // New modal component
import { StepWizardChildProps } from 'react-step-wizard';
import { SearchInput } from 'Components/Documents/SearchInput';

const AddServicesStepOne: React.FC<Partial<StepWizardChildProps>> = ({
  nextStep,
}) => {
  const [searchTerm, setSearchTerm] = useState(''); // Search term state
  const [viewMode, setViewMode] = useState<'card' | 'categorized'>('card'); // Toggle between views
  const [selectedService, setSelectedService] = useState<Service | null>(null); // For modal
  const debouncedSearchTerm = useDebouncedFilters(searchTerm); // Debounced search term
  const { data: services, isLoading } = useGetServicesQuery();
  const { data: businesses } = useGetUserBusinessesQuery();
  const [AddCartItem] = useAddCartItemServiceMutation();

  const { data: Cart } = useGetCartItemsQuery('Service');

  // Handle Add to Cart
  const handleAddCartItem = async (cartItem: {
    product_id: number;
    price_id: number;
    business_id: number | undefined;
  }) => {
    try {
      await AddCartItem({
        cart_item: {
          ...cartItem,
          business_id: cartItem.business_id ?? 0, // Pass `0` for "Personal"
        },
      }).unwrap();
      successToast('Service added to cart successfully!');
    } catch (error: any) {
      errorToast(error?.data?.message || 'Failed to add service to cart.');
    }
  };

  // Filter services based on the search term
  const filteredServices = services
    ?.filter((service: Service) =>
      service.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()),
    )
    .sort((a, b) => (a.is_bundle === b.is_bundle ? 0 : a.is_bundle ? -1 : 1));

  // Categorize services
  const categorizedServices = {
    'Accounting & Bookkeeping': filteredServices?.filter((service) =>
      /Accounting - /i.test(service.title),
    ),
    'Tax Services': filteredServices?.filter((service) =>
      /Tax - /i.test(service.title),
    ),
    'Business Formation Services': filteredServices?.filter((service) =>
      /Business Formation - /i.test(service.title),
    ),
    'Business Planning Services': filteredServices?.filter((service) =>
      /Business Planning - /i.test(service.title),
    ),
    'Payroll Services': filteredServices?.filter((service) =>
      /Payroll - /i.test(service.title),
    ),
    'Local Tax Filing Services': filteredServices?.filter((service) =>
      /Local Tax Filing - /i.test(service.title),
    ),
    'Auditing Services': filteredServices?.filter((service) =>
      /Audit - /i.test(service.title),
    ),
    'Other Services': filteredServices?.filter((service) =>
      /Others - /i.test(service.title),
    ),
  };

  // Convert businesses for dropdown options
  const businessOptions = [
    { name: 'Personal', id: undefined },
    ...(businesses?.map((b) => ({ name: b.name, id: b.id.toString() })) || []),
  ];

  return (
    <div className="flex flex-col justify-between gap-4 h-full">
      {/* SearchInput and Toggle Button */}
      <div className="flex items-center gap-4 mb-4">
        <SearchInput
          name="search"
          placeholder="Search services..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
        />
        <CustomButton
          text={viewMode === 'card' ? 'Categorized View' : 'Card View'}
          bgColor="green"
          textColor="white"
          bgColorHover="green-600"
          textColorHover="white"
          borderColor="green"
          width="min-w-max"
          onClick={() =>
            setViewMode(viewMode === 'card' ? 'categorized' : 'card')
          }
          height="h-10"
          textSize="text-sm"
          font="semibold"
        />
      </div>

      {/* Conditional Rendering for Views */}
      {viewMode === 'card' ? (
        <div className="grid grid-cols-2 gap-16 md-max:w-full md-max:grid-cols-1">
          <div className="border-r border-gray-100 h-full md-max:border-0">
            <p className="text-gray-dark font-extrabold text-regular">
              Our Services
            </p>
            <div className="overflow-auto px-4 h-96">
              {isLoading ? (
                <Spinner />
              ) : filteredServices && filteredServices.length === 0 ? (
                <p className="text-base text-gray-dark font-medium">
                  No Services Found
                </p>
              ) : (
                <div>
                  {filteredServices?.map(
                    (service: Service, index: number, array: any[]) => (
                      <div key={service.id}>
                        {!service.is_bundle && (
                          <div
                            className={`mr-6 py-8 ${
                              index + 1 === array.length
                                ? 'border-b-0'
                                : 'border-b'
                            } border-gray-100`}
                          >
                            <ServiceCard
                              service={service}
                              isBundle={false}
                              type="Service"
                            />
                          </div>
                        )}
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <p className="text-gray-dark font-extrabold text-regular">
              Our Bundles
            </p>
            <div className="overflow-auto px-4 h-96">
              {isLoading ? (
                <Spinner />
              ) : filteredServices && filteredServices.length === 0 ? (
                <p className="text-base text-gray-dark font-medium">
                  No Bundles Added Yet
                </p>
              ) : (
                filteredServices?.map((service: Service) => (
                  <div key={service.id} className="py-1">
                    {service.is_bundle && (
                      <ServiceCard
                        service={service}
                        isBundle={true}
                        type="Service"
                      />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {Object.entries(categorizedServices).map(([category, services]) => (
            <div key={category} className="border-b pb-4">
              <h2 className="text-xl font-bold mb-2">{category}</h2>
              {services && services.length > 0 ? (
                <ul>
                  {services.map((service) => (
                    <li
                      key={service.id}
                      className="flex justify-between items-center cursor-pointer hover:bg-gray-100 p-2 rounded"
                      onClick={() => setSelectedService(service)}
                    >
                      <span>{service.title}</span>
                      <span className="text-gray-600 font-semibold">
                        ${service.prices[0]?.amount_cents / 100 || 0}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No services in this category.</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Proceed Button */}
      <div className="w-1/3 md-max:w-full self-end gap-8 pb-5">
        <CustomButton
          loading={Cart?.cart_items.length === 0}
          onClick={() =>
            nextStep && Cart?.cart_items.length !== 0 && nextStep()
          }
          suffix={<NextIcon />}
        >
          <span className="text-sm w-full font-semibold">
            Proceed{' '}
            <span className="ml-16 font-normal whitespace-nowrap mr-2 px-2">
              Payment and Checkout
            </span>
          </span>
        </CustomButton>
      </div>

      {/* Service Details Modal */}
      {selectedService && (
        <ServiceDetailsModal
          service={selectedService}
          onClose={() => setSelectedService(null)}
          AddCartItem={handleAddCartItem} // Pass AddCartItem
          businesses={businessOptions} // Pass businesses
          setViewMode={setViewMode} // Pass the setter function
        />
      )}
    </div>
  );
};

export default AddServicesStepOne;
